import { useEffect, useRef } from 'react';

import { useGlobalConfig } from '@/components/GlobalContext';

import { MaturityLevels, UM_To_RTE_MaturityMap } from '@/enums';
import { log } from '@/services/appMonitoringSuite';
import { useAuth } from '@/services/auth/authContext';
import { useAppQuery } from '@/services/graphQL';
import { normalizeDeviceLanguage } from '@/utils/normalizeDeviceLanguage';

/**
 * The app query has three session-scoped inputs: language and maturity, which Apollo keys on as
 * variables, and the token, which reaches it through a header. This hook covers the third by
 * refetching on the session signal AuthProvider publishes.
 */
export const useSessionScopedAppQuery = () => {
  const { selectedProfile, sessionId } = useAuth();
  const { userLanguage, userMaturity } = useGlobalConfig();

  // GlobalContext defers userLanguage to the next navigation so UI copy does not swap
  // mid-screen. The app query has to run as the incoming profile immediately, so it reads the
  // session instead. uiLanguage comes off the profile API unnormalized.
  const sessionLanguage = selectedProfile?.uiLanguage
    ? normalizeDeviceLanguage(selectedProfile.uiLanguage)
    : userLanguage;
  const sessionMaturity = selectedProfile?.maturity
    ? UM_To_RTE_MaturityMap(selectedProfile.maturity as MaturityLevels)
    : userMaturity;

  const { app, loading, error, refetch } = useAppQuery({
    userLanguage: sessionLanguage,
    userMaturity: sessionMaturity,
  });

  const variablesKey = `${sessionLanguage}|${sessionMaturity}`;
  const lastSessionRef = useRef({ sessionId, variablesKey });

  useEffect(() => {
    const previous = lastSessionRef.current;
    lastSessionRef.current = { sessionId, variablesKey };

    if (previous.sessionId === sessionId) return;
    // Both facts change on the same commit, so a changed variablesKey means Apollo has already
    // refired. A session that changed nothing else is the one case it cannot see.
    if (previous.variablesKey !== variablesKey) return;

    refetch().catch((e) => {
      log(
        `AppQuery: session refetch failed (session ${sessionId}): ${e?.message}`,
        'error'
      );
    });
  }, [sessionId, variablesKey, refetch]);

  const lastRecoveryKeyRef = useRef<string | null>(null);

  // Backstop for an app query that failed outright, including a store clear that settles as
  // loading:false with an error and no replacement fetch. Once per session so it cannot loop.
  useEffect(() => {
    if (!error) return;

    const recoveryKey = `${sessionId}|${variablesKey}`;
    if (lastRecoveryKeyRef.current === recoveryKey) return;
    lastRecoveryKeyRef.current = recoveryKey;

    refetch().catch((e) => {
      log(
        `AppQuery: error recovery refetch failed (session ${sessionId}): ${e?.message}`,
        'error'
      );
    });
  }, [error, sessionId, variablesKey, refetch]);

  return { app, loading, error, refetch };
};
